import { z } from 'zod';

/** Required on every mutating `/sync` route: a UUID the app generates once per logical request and reuses on every retry. */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';
/** Sent by the app on every request, dotted numeric (`1.4.2`). Missing or unparseable is served without a warning. */
export const APP_VERSION_HEADER = 'X-App-Version';
/** `android` or `ios`, sent with the version on the driver app's sign-in and sync calls so the office can see which phone runs what. */
export const APP_PLATFORM_HEADER = 'X-App-Platform';
/** Set to `true` on a response to an app below the minimum supported version, with the minimum in `X-Minimum-App-Version`. */
export const APP_OUTDATED_HEADER = 'X-App-Outdated';
export const MINIMUM_APP_VERSION_HEADER = 'X-Minimum-App-Version';

/** What the app shows when it is older than the server still supports. The request was served; this is a nudge, not a refusal. */
export const appVersionNoticeSchema = z.object({
  minimumVersion: z.string(),
  message: z.string(),
});
export type AppVersionNotice = z.infer<typeof appVersionNoticeSchema>;

/** Every successful `/sync` response is `{ data, appVersion }`; `appVersion` is null unless the app is outdated. */
export function syncEnvelopeSchema<T extends z.ZodType>(data: T) {
  return z.object({ data, appVersion: appVersionNoticeSchema.nullable() });
}
export interface SyncEnvelope<T> {
  data: T;
  appVersion: AppVersionNotice | null;
}

/**
 * Why a request failed. `retriable` is the only thing the app's outbox needs to choose between backing off and
 * giving up; the code names the reason for logs and screens. The set only grows.
 */
export const SYNC_ERROR_CODES = [
  'unauthorized',
  /** The office asked for this phone to be wiped: clear local data and sign out. Answered on every call until a new sign-in replaces the device (gitaumoses4/chukua#59). */
  'device_wiped',
  'invalid_request',
  'idempotency_key_required',
  'idempotency_key_reused',
  'not_found',
  'object_not_uploaded',
  'rate_limited',
  'unavailable',
  'internal',
] as const;
export type SyncErrorCode = (typeof SYNC_ERROR_CODES)[number];

export const syncErrorSchema = z.object({
  error: z.object({
    code: z.enum(SYNC_ERROR_CODES),
    message: z.string(),
    retriable: z.boolean(),
    issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
  }),
  appVersion: appVersionNoticeSchema.nullable(),
});
export type SyncError = z.infer<typeof syncErrorSchema>;
